import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { Mail, Phone, Building2, Trash2, ChevronRight, ExternalLink } from "lucide-react";

const STATUS_LABELS = {
  novo: "Novo",
  contatado: "Contatado",
  convertido: "Convertido",
  descartado: "Descartado",
};

const STATUS_COLORS = {
  novo: "bg-foreground text-background",
  contatado: "bg-blue-600 text-white",
  convertido: "bg-emerald-600 text-white",
  descartado: "bg-foreground/20 text-foreground/60",
};

const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ novo: 0, contatado: 0, convertido: 0, descartado: 0, total: 0 });
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : "";
      const [{ data: rows }, { data: s }] = await Promise.all([
        api.get(`/platform/leads${params}`),
        api.get("/platform/leads/stats"),
      ]);
      setLeads(rows);
      setStats(s);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const updateLead = async (id, body) => {
    try {
      const { data } = await api.patch(`/platform/leads/${id}`, body);
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      toast.success("Atualizado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Remover este lead?")) return;
    try {
      await api.delete(`/platform/leads/${id}`);
      toast.success("Lead removido");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="leads-tab">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">Pipeline de Leads</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">
            {stats.total} {stats.total === 1 ? "Lead" : "Leads"}
          </h1>
        </div>
        <div className="grid grid-cols-4 gap-0 border-t border-l border-border self-start">
          {["novo", "contatado", "convertido", "descartado"].map((k) => (
            <button
              key={k}
              onClick={() => setFilter(filter === k ? "" : k)}
              className={`px-4 py-3 border-r border-b border-border text-left min-w-[110px] ${
                filter === k ? "bg-foreground text-background" : "bg-background hover:bg-muted/40"
              }`}
              data-testid={`leads-filter-${k}`}
            >
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{STATUS_LABELS[k]}</div>
              <div className="font-heading text-2xl font-black tracking-tighter">{stats[k]}</div>
            </button>
          ))}
        </div>
      </div>

      {filter && (
        <button
          onClick={() => setFilter("")}
          className="mb-4 text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-foreground"
          data-testid="leads-clear-filter"
        >
          ← Limpar filtro: {STATUS_LABELS[filter]}
        </button>
      )}

      {loading ? (
        <div className="text-foreground/50">Carregando…</div>
      ) : leads.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center" data-testid="leads-empty">
          <Mail className="w-10 h-10 mx-auto mb-4 opacity-40" />
          <div className="text-sm uppercase tracking-[0.2em] text-foreground/60">
            Nenhum lead {filter ? `com status "${STATUS_LABELS[filter]}"` : "ainda"}
          </div>
        </div>
      ) : (
        <div className="border-t border-border">
          {leads.map((l) => {
            const isOpen = expanded === l.id;
            return (
              <div key={l.id} className="border-b border-border" data-testid={`lead-row-${l.id}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : l.id)}
                  className="w-full flex items-center justify-between gap-4 px-2 md:px-4 py-5 hover:bg-muted/40 transition-colors text-left"
                  data-testid={`lead-toggle-${l.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <ChevronRight className={`w-5 h-5 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <div className="min-w-0">
                      <div className="font-heading text-xl md:text-2xl font-black tracking-tighter truncate">
                        {l.name}
                      </div>
                      <div className="text-xs text-foreground/60 mt-1 flex items-center gap-3 flex-wrap">
                        <span className="font-mono truncate max-w-[200px]">{l.email}</span>
                        {l.company && (
                          <>
                            <span>·</span>
                            <span className="truncate max-w-[180px]">{l.company}</span>
                          </>
                        )}
                        {l.plan_interest && l.plan_interest !== "nao_sei" && (
                          <>
                            <span>·</span>
                            <span className="uppercase tracking-wider">{l.plan_interest}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${STATUS_COLORS[l.status] || ""}`}
                      data-testid={`lead-status-${l.id}`}
                    >
                      {STATUS_LABELS[l.status]}
                    </span>
                    <span className="hidden md:inline text-xs text-foreground/40">
                      {new Date(l.created_at).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-2 md:px-6 pb-6 bg-muted/20 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
                        <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 pt-0.5">
                          <Mail className="w-3 h-3 inline mr-1" /> Email
                        </div>
                        <a
                          href={`mailto:${l.email}`}
                          className="font-mono break-all hover:underline"
                          data-testid={`lead-email-link-${l.id}`}
                        >
                          {l.email}
                        </a>

                        {l.phone && (
                          <>
                            <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 pt-0.5">
                              <Phone className="w-3 h-3 inline mr-1" /> Telefone
                            </div>
                            <a href={`tel:${l.phone}`} className="font-mono hover:underline">
                              {l.phone}
                            </a>
                          </>
                        )}

                        {l.company && (
                          <>
                            <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 pt-0.5">
                              <Building2 className="w-3 h-3 inline mr-1" /> Empresa
                            </div>
                            <div>{l.company}</div>
                          </>
                        )}

                        <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 pt-0.5">Plano</div>
                        <div className="uppercase tracking-wider">{l.plan_interest || "—"}</div>

                        <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 pt-0.5">Origem</div>
                        <div>{l.source || "—"}</div>

                        <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 pt-0.5">Criado</div>
                        <div className="font-mono text-xs">
                          {new Date(l.created_at).toLocaleString("pt-BR")}
                        </div>

                        <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 pt-0.5">Email enviado</div>
                        <div>
                          {l.email_sent ? (
                            <span className="text-emerald-600">✓ sim</span>
                          ) : (
                            <span className="text-foreground/50">não (SMTP off ou falhou)</span>
                          )}
                        </div>
                      </div>

                      {l.message && (
                        <div>
                          <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">
                            Mensagem
                          </div>
                          <div className="border border-border bg-background p-3 text-sm whitespace-pre-wrap">
                            {l.message}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em]">Status</Label>
                        <Select
                          value={l.status}
                          onValueChange={(v) => updateLead(l.id, { status: v })}
                        >
                          <SelectTrigger className="mt-2 h-11 rounded-none" data-testid={`lead-status-select-${l.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>
                                {v}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em]">Notas internas</Label>
                        <Textarea
                          value={notesDraft[l.id] ?? l.notes ?? ""}
                          onChange={(e) =>
                            setNotesDraft((p) => ({ ...p, [l.id]: e.target.value }))
                          }
                          className="mt-2 rounded-none min-h-[100px]"
                          placeholder="Anotações sobre o contato, próximos passos…"
                          data-testid={`lead-notes-${l.id}`}
                        />
                        <Button
                          size="sm"
                          className="rounded-none mt-2 uppercase tracking-[0.2em] text-xs"
                          onClick={() =>
                            updateLead(l.id, { notes: notesDraft[l.id] ?? l.notes ?? "" })
                          }
                          data-testid={`lead-save-notes-${l.id}`}
                        >
                          Salvar notas
                        </Button>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                        <a href={`mailto:${l.email}`} className="inline-flex">
                          <Button variant="outline" size="sm" className="rounded-none">
                            <ExternalLink className="w-3 h-3 mr-1" /> Email
                          </Button>
                        </a>
                        {l.phone && (
                          <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex">
                            <Button variant="outline" size="sm" className="rounded-none">
                              <ExternalLink className="w-3 h-3 mr-1" /> WhatsApp
                            </Button>
                          </a>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          className="rounded-none ml-auto"
                          onClick={() => deleteLead(l.id)}
                          data-testid={`lead-delete-${l.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Remover
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadsTab;
